/**
 * API client.
 *
 * One place that knows about four backend conventions, so no screen has to:
 *
 *   1. Response envelope — every response is
 *      `{ success, data, meta }` on success and
 *      `{ success: false, error: { code, message, fields } }` on failure.
 *      Callers get `data` directly, or an ApiError they can branch on by `code`.
 *
 *   2. Tree context — most endpoints operate inside one family tree. The active
 *      tree goes out as `X-Tree-ID` on every request. This is how the tree
 *      switcher works without re-authenticating.
 *
 *   3. JWT with refresh — a 401 triggers exactly one refresh attempt and a
 *      replay. Concurrent 401s share that single refresh rather than each
 *      firing one, which would rotate the refresh token N times and blacklist
 *      it out from under the others.
 *
 *   4. Idempotency — money, invitation, and merge mutations carry an
 *      `Idempotency-Key`, so a relative on a bad connection tapping "send" twice
 *      does not send twice.
 */

import { session } from '../state/session.js';

const API_BASE = '/api/v1';

/** Paths where a replayed request must not create a second side effect. */
const IDEMPOTENT_PREFIXES = [
  '/payments/',
  '/care/funds/',
  '/trees/invitations/',
  '/people/merges/',
];

/** Endpoints that must never carry an Authorization header. */
const ANONYMOUS_PATHS = [
  '/auth/token/',
  '/auth/token/refresh/',
  // Carries the refresh token in its body and needs no access token. Sending one
  // would drag an expired session through the 401-refresh dance on the way out.
  '/auth/logout/',
  '/auth/register/',
  '/auth/password/reset/',
  '/auth/password/reset/confirm/',
  '/auth/invitations/preview/',
  '/health/',
  // The plaque on a headstone. Anonymous is the whole point: the visitor it exists for is
  // standing in a cemetery with no account, and a tree header would be a header for a family
  // they may not belong to.
  '/people/remember/',
];

export class ApiError extends Error {
  /**
   * @param {string} code    stable machine-readable identifier
   * @param {string} message safe to show a user
   * @param {object|null} fields per-field validation errors
   * @param {number} status  HTTP status
   */
  constructor(code, message, fields, status) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.fields = fields ?? null;
    this.status = status;
  }

  /** True when the caller asked for a tree they are not a member of. */
  get isTreeAccessDenied() {
    return this.code === 'tree_access_denied';
  }

  /** True when this family has used its storage allowance. */
  get isQuotaExceeded() {
    return this.code === 'storage_quota_exceeded';
  }

  /** True when Keeper hit this family's monthly usage limit. */
  get isKeeperBudgetExceeded() {
    return this.code === 'keeper_budget_exceeded';
  }
}

/** Shared refresh promise, so N concurrent 401s cause one refresh. */
let refreshInFlight = null;

function isAnonymous(path) {
  return ANONYMOUS_PATHS.some((prefix) => path.startsWith(prefix));
}

function needsIdempotencyKey(path, method) {
  if (!['POST', 'PATCH', 'PUT', 'DELETE'].includes(method)) return false;
  return IDEMPOTENT_PREFIXES.some((prefix) => path.startsWith(prefix));
}

function buildHeaders(path, method, { anonymous, hasBody, isFormData, idempotencyKey }) {
  const headers = {};

  // FormData sets its own Content-Type, including the multipart boundary the server needs to
  // parse it. Setting it here overrides that boundary and the upload arrives unreadable — a
  // failure that looks like a server fault and is not.
  if (hasBody && !isFormData) headers['Content-Type'] = 'application/json';

  if (!anonymous && session.accessToken) {
    headers.Authorization = `Bearer ${session.accessToken}`;
  }

  // Sent on tree-scoped requests only. The server re-validates it against live
  // memberships, so a stale value fails cleanly rather than leaking — but it fails
  // for the *whole request*, and that is why the exclusions below matter.
  //
  // `/auth/me/` and `/trees/` are tree-agnostic: they exist precisely to tell a user
  // which families they belong to, before any one of them is active. Sending a stale
  // tree id with them made the middleware refuse the one call that would have
  // discovered the id was stale — so the tree switcher showed "No family trees yet"
  // and every screen reported no access, with no way out but signing out.
  if (!anonymous && session.activeTreeId && !isTreeAgnostic(path)) {
    headers['X-Tree-ID'] = session.activeTreeId;
  }

  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  return headers;
}

/**
 * Paths that never operate inside one family tree.
 *
 * Mirrors `TREE_AGNOSTIC_PREFIXES` in coresite/middleware/tree_context.py. The two
 * lists have to agree: anything the server treats as tree-agnostic must not be sent
 * a tree header, or a stale header turns a working endpoint into a 403.
 */
const TREE_AGNOSTIC = [
  '/health/',
  '/auth/token/',
  '/auth/register/',
  '/auth/password/',
  '/auth/me/',
  '/trees/',
];

function isTreeAgnostic(path) {
  const clean = path.split('?')[0];
  if (clean.startsWith('/people/remember/')) return true;
  // `/trees/` is agnostic, but `/trees/current/` and `/trees/members/` are not.
  if (clean === '/trees/' || clean === '/trees') return true;
  return TREE_AGNOSTIC.some((prefix) => prefix !== '/trees/' && clean.startsWith(prefix));
}

async function parseResponse(response) {
  // 204 and empty bodies are legitimate (DELETE, cancel-deletion).
  const text = await response.text();
  if (!text) return { success: response.ok, data: null, meta: {} };

  try {
    return JSON.parse(text);
  } catch {
    throw new ApiError('invalid_response', unreadable(response, text), null, response.status);
  }
}

/**
 * Say what came back instead of JSON.
 *
 * **"The server sent something we could not read" is a dead end**, and it was a real one:
 * two people spent an evening unable to sign in or create an account with no more to go on
 * than that sentence. It is the same message whether the API is down, behind a gateway
 * error, or simply not wired up — and those have completely different fixes.
 *
 * Which one it is shows in the status: 502 or 504 is a gateway with nothing behind it,
 * 500 is the server falling over and rendering its own error page, and 200 with HTML is
 * the single-page fallback answering a path that never reached the API. So the message
 * carries the status and says a page came back — and stops short of naming a cause,
 * because the first guess at one here was wrong and sent somebody to the wrong file.
 *
 * Nothing from the body is shown. A response that is not ours is not something to quote
 * back onto the screen; the status and the shape are enough to tell these apart, and the
 * body is left in the console for whoever is looking.
 */
function unreadable(response, text) {
  const start = text.trim().slice(0, 80).toLowerCase();
  const isPage = start.startsWith('<!doctype') || start.startsWith('<html');

  if (typeof console !== 'undefined') {
    console.error(
      `[linyay] ${response.status} from ${response.url} was not JSON:`,
      text.slice(0, 400),
    );
  }

  if (isPage) {
    return (
      `The API did not answer — a web page came back instead (status ${response.status}). ` +
      'The server or whatever sits in front of it needs looking at.'
    );
  }

  if (response.status >= 500) {
    return `The server failed and said nothing we can read (status ${response.status}).`;
  }

  return `The server sent something we could not read (status ${response.status}).`;
}

/**
 * Trade the stored refresh token for an access token.
 *
 * Exported so boot can call it *before* its first request. The access token lives in
 * memory only, so after a reload there is none — and the first call then went out
 * bare, came back 401, refreshed, and replayed. Three requests where two do, on
 * every single page load, plus a red 401 in the console that made real errors easy
 * to miss.
 */
export async function refreshAccessToken() {
  // Collapse concurrent refreshes. Refresh tokens rotate and the old one is
  // blacklisted, so two parallel refreshes would invalidate each other.
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    if (!session.refreshToken) return false;

    const response = await fetch(`${API_BASE}/auth/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh: session.refreshToken }),
    });

    if (!response.ok) {
      session.clear();
      return false;
    }

    const payload = await parseResponse(response);
    const data = payload.data ?? payload;

    session.setTokens({ access: data.access, refresh: data.refresh ?? session.refreshToken });
    return true;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

/**
 * Make an API request.
 *
 * @param {string} path   path below /api/v1, e.g. '/people/'
 * @param {object} options
 * @param {string} [options.method='GET']
 * @param {object} [options.body]         serialised as JSON
 * @param {object} [options.query]        appended as a querystring
 * @param {boolean} [options.anonymous]   force no Authorization header
 * @param {boolean} [options.raw=false]   return the whole envelope, not just data
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<any>} the `data` field, or the whole envelope when raw
 */
export async function request(path, options = {}) {
  const {
    method = 'GET',
    body,
    query,
    anonymous = isAnonymous(path),
    raw = false,
    signal,
    _isRetry = false,
  } = options;

  let url = `${API_BASE}${path}`;

  if (query) {
    const params = new URLSearchParams(
      Object.entries(query).filter(([, value]) => value !== undefined && value !== null),
    );
    const queryString = params.toString();
    if (queryString) url += `?${queryString}`;
  }

  const idempotencyKey = needsIdempotencyKey(path, method)
    ? (options.idempotencyKey ?? crypto.randomUUID())
    : undefined;

  const response = await fetch(url, {
    method,
    headers: buildHeaders(path, method, {
      anonymous,
      hasBody: body !== undefined,
      isFormData: body instanceof FormData,
      idempotencyKey,
    }),
    // FormData goes as-is. Stringifying it produces the literal "[object FormData]", which the
    // server accepts as a body and then finds empty.
    body: body === undefined ? undefined : body instanceof FormData ? body : JSON.stringify(body),
    signal,
  });

  // A 401 on an authenticated request means the short-lived access token
  // expired. Refresh once and replay; never loop.
  if (response.status === 401 && !anonymous && !_isRetry) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      return request(path, { ...options, _isRetry: true, idempotencyKey });
    }
  }

  const payload = await parseResponse(response);

  if (!response.ok || payload.success === false) {
    const error = payload.error ?? {};

    // A refused tree is recoverable, and the client should recover rather than
    // report a dead end on every screen. It happens for ordinary reasons: an
    // administrator removed the relative, the tree was deleted, or a developer
    // reseeded and the stored id no longer exists.
    //
    // Drop the stale id, re-read which families this user actually belongs to, and
    // replay once. Only once — a second failure is a real refusal.
    if (error.code === 'tree_access_denied' && !anonymous && !_isRetry) {
      const recovered = await recoverFromRefusedTree();
      if (recovered === 'signed-out') {
        throw new ApiError(
          'signed_out',
          'Your session has ended. Sign in again to carry on.',
          undefined,
          401,
        );
      }
      if (recovered) {
        return request(path, { ...options, _isRetry: true, idempotencyKey });
      }
    }

    // **No code means this did not come from Linyay.** Every failure our server sends carries
    // one — `envelope_exception_handler` sees to that — so a body with none is a proxy, a
    // gateway, or a dev server answering for a backend that is not running. Vite returns an
    // empty 500 in exactly that case.
    //
    // It used to fall through to "Something went wrong.", which is both unhelpful and untrue:
    // nothing went wrong with the request, there was nobody to send it to. The sign-in screen
    // has had the right sentence all along — *"We could not reach Linyay"* — in the branch for
    // a `fetch` that throws, and a response like this never reached it.
    if (!error.code) {
      throw new ApiError(
        'unreachable',
        'We could not reach Linyay. Check your connection and try again.',
        undefined,
        response.status,
      );
    }

    throw new ApiError(error.code, error.message ?? 'Something went wrong.', error.fields, response.status);
  }

  return raw ? payload : payload.data;
}

/**
 * Recover from a tree the server will not serve.
 *
 * Clears the stored id, reloads the user's real membership list, and adopts the
 * first tree they are actually in. Returns whether a different tree was adopted, so
 * the caller knows whether replaying is worth it.
 */
let recoveryInFlight = null;

async function recoverFromRefusedTree() {
  // Collapse concurrent recoveries, exactly as `refreshAccessToken` does.
  //
  // The home screen fires two requests at once. When the stored tree is dead both come
  // back refused, and both used to start recovering: the first cleared the id, adopted
  // the real tree, and replayed — then the second cleared the id *again*, wiping the
  // tree that had just been adopted. The replay then went out with no header at all and
  // fell back to the token's `tree_id` claim, which still named the dead tree. So the
  // recovery undid itself, and every screen stayed refused.
  if (recoveryInFlight) return recoveryInFlight;

  recoveryInFlight = doRecoverFromRefusedTree().finally(() => {
    recoveryInFlight = null;
  });

  return recoveryInFlight;
}

async function doRecoverFromRefusedTree() {
  const refused = session.activeTreeId;
  session.setContext({ treeId: null });

  try {
    // Tree-agnostic, so this call now goes out without the poisoned header.
    const me = await request('/auth/me/', { method: 'GET', _isRetry: true });
    session.setUser(me);
  } catch (why) {
    // **A dead session is not a refused tree**, and saying so matters: "you do not have
    // access to this family tree" tells somebody they lack permission when what they need is
    // to sign in again. They would never think to reload.
    //
    // Found on a tab left open overnight: the token expired, the tree check answered 403, the
    // recovery met a 401 here, and the screen reported the 403 it started with.
    if (why?.status === 401) return 'signed-out';
    return false;
  }

  const adopted = session.activeTreeId;

  if (adopted && adopted !== refused) {
    console.info('Switched away from a family tree that is no longer available.');
    return true;
  }

  // No memberships at all. Replaying would fall back to the token's stale claim and be
  // refused again, so send them somewhere they can act instead of failing every screen.
  if (!adopted) {
    session.emit('tree-changed');
  }

  return false;
}

export const api = {
  get: (path, options) => request(path, { ...options, method: 'GET' }),
  post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
  patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
  put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
  delete: (path, options) => request(path, { ...options, method: 'DELETE' }),

  /**
   * Fetch a paginated list, returning items plus the cursor.
   *
   * Cursor pagination is the backend default because family lists grow at the
   * top — new photos, new feed posts — and offset pagination on a list that
   * grows while you read it skips and repeats rows.
   */
  async list(path, options = {}) {
    const envelope = await request(path, { ...options, method: 'GET', raw: true });
    return {
      items: envelope.data ?? [],
      next: envelope.meta?.pagination?.next ?? null,
      previous: envelope.meta?.pagination?.previous ?? null,
      count: envelope.meta?.pagination?.count ?? null,
    };
  },
};
